// Guided setup, step 3: Onboarding checklist. Assigns an EXISTING onboarding template directly (the same AssignChecklistForm and server guards as the employee's
// Onboarding tab), shows what is already assigned (so Back / resume never assigns twice), or records that no onboarding checklist applies.
// No template editing happens here; "Manage checklist templates" opens the existing administration and returns to THIS step.
//
// Cycle 13 layout (same organisation as the employee's Onboarding tab, one shared implementation: checklist/ChecklistWorkArea.tsx): heading on the left; on the right, above the
// card, a smaller "Manage checklist templates" link next to the primary "+ Assign checklist" button. The button opens a compact panel beneath it (Cancel sends nothing; success
// closes it, refreshes the list and shows a short transient confirmation). The "Assigned checklists" card holds ONLY assigned checklists (name, kind, assigned date, "N of M tasks
// done", read-only task rows, "View tasks" to open that exact checklist on the employee's page); with nothing assigned it shows the empty state. "No onboarding checklist
// applies" stays a compact acknowledgment in the shell footer, offered only when the area is readable, loaded and empty.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listHrChecklistAssignmentsForEmployee, listHrChecklistTemplates, type HrChecklistAssignment, type HrChecklistTemplate } from '../../../api/client';
import { AssignChecklistForm } from '../AssignChecklistForm';
import { ChecklistAssignmentsPanel } from '../ChecklistAssignmentsPanel';
import { AssignedChecklistsCard, ChecklistWorkArea } from '../checklist/ChecklistWorkArea';
import { progressLabel } from '../checklist/checklistModel';
import { classifyLoadError, PERMISSION_LABEL, useSurfaceFlag } from '../workspace/loadState';
import { formatLocalDate } from '../hrFormat';
import { UnavailableNotice } from './stepUi';
import { employeePath, setupPath } from './setupModel';
import type { StepContext } from './setupTypes';

type ListState = 'loading' | 'ready' | 'denied' | 'error';

const HELP_BODY = (
  <>
    <p>Assign the onboarding checklist this person works through when they start. Task due dates are counted from the date you choose, which starts as the hire date.</p>
    <p className="mt-2">Assigning a checklist does not complete any task. Use <strong>View tasks</strong> to open that exact checklist on the employee’s page, then come back to setup.</p>
    <p className="mt-2">Only choose “No onboarding checklist applies” when nothing is assigned. It records a review only and can be undone.</p>
  </>
);

/** Where "View tasks" goes: this employee's own page, on the Onboarding tab, with THAT assignment highlighted (the page already understands section / tab / item). */
export function viewTasksPath(employeeId: string, assignmentId: string): string {
  return employeePath(employeeId, `?section=checklists&tab=onboarding&item=${encodeURIComponent(assignmentId)}`);
}

const VIEW_BTN = 'text-xs font-medium border border-gray-300 bg-white text-gray-700 rounded-md px-3 py-1.5 hover:bg-gray-50 whitespace-nowrap';

export function ChecklistStep({ ctx }: { ctx: StepContext }) {
  const c = ctx.view.summary.checklist;
  const savedDecided = ctx.view.progress?.decisions.checklist?.decision === 'not_applicable';
  const [templates, setTemplates] = useState<HrChecklistTemplate[]>([]);
  const [templatesState, setTemplatesState] = useState<ListState>('loading');
  const [assignments, setAssignments] = useState<HrChecklistAssignment[]>([]);
  const [assignmentsState, setAssignmentsState] = useState<ListState>('loading');
  const mayManageTemplates = useSurfaceFlag('mayManageChecklistTemplates') !== false;
  const navigate = useNavigate();

  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const loadTemplates = useCallback(() => {
    setTemplatesState('loading');
    listHrChecklistTemplates()
      .then((d) => { setTemplates(d.templates.filter((t) => !t.retiredAt && t.processKind === 'onboarding')); setTemplatesState('ready'); })
      .catch((e) => setTemplatesState(classifyLoadError(e, { gatedList: true }).kind === 'denied' ? 'denied' : 'error'));
  }, []);
  const loadAssignments = useCallback(() => {
    return listHrChecklistAssignmentsForEmployee(ctx.employeeId)
      .then((d) => { setAssignments(d.assignments); setAssignmentsState('ready'); })
      // A failed REFRESH keeps what is shown; only a first failed read is an error. An error is never drawn as "none assigned" and never allows "none applies".
      .catch((e) => setAssignmentsState((s) => (s === 'ready' ? 'ready' : ['denied', 'targetUnavailable'].includes(classifyLoadError(e, { targeted: true }).kind) ? 'denied' : 'error')));
  }, [ctx.employeeId]);
  useEffect(() => { loadTemplates(); void loadAssignments(); }, [loadTemplates, loadAssignments]);

  const available = c.state !== 'unavailable';
  // The saved, authoritative list (server summary) is the fallback; the fuller read above (with tasks) is what the card shows once it has loaded, and is used for the duplicate hint.
  const summaryRows = c.state === 'unavailable' ? [] : c.assignments.filter((a) => a.processKind === 'onboarding')
    .slice().sort((a, b) => new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime());
  const fullOnboarding = assignments.filter((a) => a.processKindSnapshot === 'onboarding');
  const hasAssigned = summaryRows.length > 0 || fullOnboarding.length > 0;
  const serverAllows = ctx.view.summary.noneNeeded?.checklist.allowed ?? (c.state === 'available' && c.onboardingCount === 0);
  const loadedOk = available && assignmentsState === 'ready' && templatesState === 'ready';
  const ackOffered = loadedOk && serverAllows && !hasAssigned;

  // ── Footer acknowledgment: "No onboarding checklist applies". State lives here; Save & continue records it with the same action. ──────────────────────────────────────────
  const [ackChecked, setAckChecked] = useState(savedDecided);
  const ackRef = useRef(ackChecked); ackRef.current = ackChecked;
  const savedRef = useRef(savedDecided); savedRef.current = savedDecided;
  const offeredRef = useRef(ackOffered); offeredRef.current = ackOffered;
  const hasAssignedRef = useRef(hasAssigned); hasAssignedRef.current = hasAssigned;
  // Assigning a checklist ends the "none applies" choice; it can never stay selected beside a real assignment.
  useEffect(() => { if (hasAssigned) setAckChecked(false); }, [hasAssigned]);

  useEffect(() => {
    if (!available) { ctx.bindSaver(null); ctx.setDirty(false); return; }
    ctx.bindSaver(async () => {
      if (hasAssignedRef.current) return savedRef.current ? { ok: true, decisions: { checklist: null } } : { ok: true }; // a stale “none applies” beside a real assignment is cleared; nothing else is touched
      if (!offeredRef.current) return { ok: true };
      if (ackRef.current && !savedRef.current) return { ok: true, decisions: { checklist: { decision: 'not_applicable' } } };
      if (!ackRef.current && savedRef.current) return { ok: true, decisions: { checklist: null } }; // deliberate undo; no record is touched
      return { ok: true };
    });
    return () => ctx.bindSaver(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  useEffect(() => {
    ctxRef.current.bindFooter({
      ...(ackOffered ? { ack: { id: 'checklist-none-applies', label: 'No onboarding checklist applies', checked: ackChecked, onChange: setAckChecked } } : {}),
      help: { title: 'How onboarding checklists work', body: HELP_BODY },
    });
  }, [ackOffered, ackChecked]);
  useEffect(() => () => ctxRef.current.bindFooter(null), []);

  const personLabel = ctx.person.name && ctx.person.name !== 'This person' ? `${ctx.person.name}’s setup` : 'setup';
  const returnState = { returnTo: setupPath(ctx.employeeId, 'checklist'), employeeName: personLabel };
  const canOpen = available && templatesState === 'ready' && templates.length > 0;
  const openHint = templatesState === 'ready' && templates.length === 0 ? 'No onboarding checklist templates exist yet.' : templatesState !== 'ready' ? 'The onboarding templates must load first.' : undefined;

  const manageLink = mayManageTemplates
    ? <Link to="/hr/admin/checklists" state={returnState} className="text-xs text-[#176A9B] hover:underline" data-testid="manage-checklist-templates">Manage checklist templates</Link>
    : null;

  const showFull = assignmentsState === 'ready' && fullOnboarding.length > 0;
  const showSummary = !showFull && summaryRows.length > 0;
  const empty = !showFull && !showSummary && assignmentsState === 'ready';

  const viewTasks = (assignmentId: string, name: string) => (
    <Link to={viewTasksPath(ctx.employeeId, assignmentId)} state={returnState} className={VIEW_BTN} data-testid="checklist-view-tasks">
      View tasks<span className="sr-only"> for {name}</span>
    </Link>
  );

  return (
    <div data-testid="step-checklist">
      <ChecklistWorkArea
        kind="onboarding" manageLink={manageLink} canOpen={canOpen} openHint={openHint} hideActions={!available}
        intro="Assign the checklist this person works through when they start. Assigning does not complete any task."
        assignPanel={(api) => (
          <AssignChecklistForm
            employeeId={ctx.employeeId} templates={templates} existingAssignments={assignments} kind="onboarding"
            defaultAnchorDate={ctx.view.summary.details.hireDate ?? undefined}
            onAssigned={() => { api.succeeded('Checklist assigned.'); void Promise.all([loadAssignments(), ctxRef.current.reload()]); }}
            onCancel={api.close}
            onViewExisting={(id) => navigate(viewTasksPath(ctx.employeeId, id), { state: returnState })}
          />
        )}
      >
        {c.state === 'unavailable' ? (
          <UnavailableNotice title="The onboarding checklist" reason={c.reason} capability={c.capability} />
        ) : (
          <>
            {templatesState === 'loading' && <p className="text-sm text-gray-400 mb-3" data-testid="checklist-templates-loading">Loading onboarding templates…</p>}
            {templatesState === 'denied' && (
              <p role="alert" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="checklist-templates-denied">
                Onboarding templates can’t be listed with your permissions. Assigning from here needs “{PERMISSION_LABEL.checklistTemplates}”.
              </p>
            )}
            {templatesState === 'error' && (
              <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3" data-testid="checklist-templates-error">
                The onboarding templates could not be loaded. This is not the same as there being none. <button type="button" className="underline" onClick={loadTemplates}>Try again</button>
              </p>
            )}
            {templatesState === 'ready' && templates.length === 0 && !hasAssigned && (
              <p className="text-sm text-gray-600 mb-3" data-testid="checklist-no-templates">No onboarding checklist templates exist yet. Create one with “Manage checklist templates”, or record that none applies.</p>
            )}

            <AssignedChecklistsCard kind="onboarding" empty={empty}>
              {assignmentsState === 'loading' && !showSummary && <p className="text-sm text-gray-400" data-testid="checklist-assignments-loading">Loading assigned checklists…</p>}
              {assignmentsState === 'denied' && (
                <p role="alert" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="checklist-assignments-denied">
                  This person’s checklists can’t be read with your permissions, so “no checklist applies” can’t be recorded here.
                </p>
              )}
              {assignmentsState === 'error' && (
                <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="checklist-assignments-error">
                  This person’s checklists could not be loaded. This is not the same as none being assigned. <button type="button" className="underline" onClick={() => { setAssignmentsState('loading'); void loadAssignments(); }}>Try again</button>
                </p>
              )}
              {showFull && (
                <div data-testid="checklist-assigned-list">
                  <ChecklistAssignmentsPanel
                    assignments={fullOnboarding} variant="summary" showHelp={false}
                    renderViewTasks={(a) => viewTasks(a.assignmentId, a.templateNameSnapshot)}
                  />
                </div>
              )}
              {showSummary && (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md mt-2" data-testid="checklist-assigned-list" aria-label="Assigned onboarding checklists">
                  {summaryRows.map((a) => (
                    <li key={a.assignmentId} className="px-4 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-testid="checklist-assigned-row">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-900 break-words">{a.templateName}</span>
                        <div className="text-xs text-gray-600 mt-0.5">Assigned {formatLocalDate(a.assignedAt)} · {progressLabel(a.completedCount, a.taskCount)}</div>
                      </div>
                      {viewTasks(a.assignmentId, a.templateName)}
                    </li>
                  ))}
                </ul>
              )}
            </AssignedChecklistsCard>
          </>
        )}
      </ChecklistWorkArea>
    </div>
  );
}
